/**
 * Meniul mobil — stratul plin ecran „coborârea în abis".
 * Doar clase CSS (fără GSAP), ca să funcționeze identic pe
 * toate profilurile (full / lite / static) și pe paginile interioare.
 */
export function initMobileMenu() {
  const nav = document.getElementById('psi-nav')
  const btn = document.getElementById('psi-menu-toggle')
  const menu = document.getElementById('psi-menu')
  if (!nav || !btn || !menu) return

  let open = false

  const scroller = menu.querySelector('.psi-menu-scroll')

  // dropdown-urile din meniu — fiecare buton își controlează wrap-ul prin aria-controls
  const subToggles = Array.from(menu.querySelectorAll('button[aria-controls]'))

  // regiunile paginii devin inerte cât e deschis meniul (focus + screen readers)
  const pageRegions = ['.psi-main', '.psi-footer']
    .map((sel) => document.querySelector(sel))
    .filter(Boolean)

  // indiciu vizual că mai e conținut sub linia de plutire
  const updateScrollHint = () => {
    if (!scroller) return
    const more = scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 8
    scroller.classList.toggle('can-scroll-more', more)
  }
  if (scroller) scroller.addEventListener('scroll', updateScrollHint, { passive: true })

  const setSub = (toggle, openSub) => {
    const wrap = document.getElementById(toggle.getAttribute('aria-controls'))
    if (!wrap) return
    wrap.classList.toggle('is-open', openSub)
    toggle.setAttribute('aria-expanded', String(openSub))

    if (openSub && scroller) {
      // secțiunea desfăcută urcă sub header — utilizatorul îi vede conținutul
      setTimeout(() => {
        scroller.scrollTo({ top: Math.max(0, toggle.offsetTop - 96), behavior: 'smooth' })
      }, 140)
    }
    // fade-ul de jos se recalculează după ce animația de desfacere se așază
    setTimeout(updateScrollHint, 420)
  }

  const setState = (next) => {
    if (open === next) return
    open = next
    nav.classList.toggle('is-menu-open', open)
    menu.classList.toggle('is-open', open)
    btn.setAttribute('aria-expanded', String(open))
    btn.setAttribute('aria-label', open ? btn.dataset.labelClose : btn.dataset.labelOpen)
    document.documentElement.classList.toggle('psi-no-scroll', open)
    pageRegions.forEach((el) => { el.inert = open })

    // meniul se redeschide mereu cu toate dropdown-urile pliate, scrollat sus
    if (!open) {
      subToggles.forEach((t) => setSub(t, false))
      if (scroller) scroller.scrollTop = 0
    } else {
      setTimeout(updateScrollHint, 100)
    }

    // NU oprim Lenis: stop() blochează touch-ul pe tot window-ul (inclusiv
    // scroll-ul intern al meniului). Pagina e blocată de overflow:hidden,
    // iar meniul are data-lenis-prevent.
  }

  subToggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      setSub(toggle, toggle.getAttribute('aria-expanded') !== 'true')
    })
  })

  btn.addEventListener('click', () => setState(!open))

  // orice link din meniu îl închide (navigarea urmează imediat)
  menu.querySelectorAll('a[href]').forEach((a) => {
    a.addEventListener('click', () => setState(false))
  })

  // Escape închide și readuce focusul pe buton
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && open) {
      setState(false)
      btn.focus()
    }
  })

  // la trecerea pe desktop se închide singur (evită scroll blocat)
  const mq = window.matchMedia('(min-width: 768px)')
  const onChange = (e) => { if (e.matches) setState(false) }
  if (mq.addEventListener) mq.addEventListener('change', onChange)
  else mq.addListener(onChange)
}

/**
 * Mega menu-urile din nav (desktop) — Specialiști, Servicii, limba.
 * Linkurile se deschid la hover (cu prag de intenție) și focus; pe touch
 * (tabletă) primul tap deschide panoul, al doilea navighează.
 * Butoanele (limba) comută la click. Escape închide.
 */
export function initMegaMenu() {
  const coarse = window.matchMedia('(pointer: coarse)').matches

  document.querySelectorAll('.psi-nav-item--mega').forEach((wrap) => {
    const trigger = wrap.querySelector('[aria-haspopup="true"]')
    const isButton = trigger && trigger.tagName === 'BUTTON'
    let closeTimer = null
    let openTimer = null

    const open = () => {
      clearTimeout(closeTimer)
      wrap.classList.add('is-open')
      if (trigger) trigger.setAttribute('aria-expanded', 'true')
    }

    const close = (delay = 160) => {
      clearTimeout(openTimer)
      clearTimeout(closeTimer)
      closeTimer = setTimeout(() => {
        wrap.classList.remove('is-open')
        if (trigger) trigger.setAttribute('aria-expanded', 'false')
      }, delay)
    }

    if (isButton) {
      // buton (limba, contul): click comută starea. Cu data-psi-hover („Servicii”, 02.10.2026: nu duce nicăieri, doar
      // deschide lista) pe mouse se deschide și la hover, ca linkurile cu panou, iar clicul îl ține deschis
      const hoverToo = !coarse && trigger.hasAttribute('data-psi-hover')
      trigger.addEventListener('click', () => {
        if (hoverToo) return open()
        wrap.classList.contains('is-open') ? close(0) : open()
      })
      if (hoverToo) {
        wrap.addEventListener('mouseenter', () => {
          clearTimeout(openTimer)
          openTimer = setTimeout(open, 80)
        })
        wrap.addEventListener('mouseleave', () => close())
        wrap.addEventListener('focusin', open)
      }
    } else if (coarse) {
      // touch (tabletă): primul tap deschide panoul, al doilea navighează
      trigger.addEventListener('click', (e) => {
        if (!wrap.classList.contains('is-open')) {
          e.preventDefault()
          open()
        }
      })
    } else {
      // link (Specialiști/Servicii): hover cu prag de intenție + focus tastatură
      wrap.addEventListener('mouseenter', () => {
        clearTimeout(openTimer)
        openTimer = setTimeout(open, 80)
      })
      wrap.addEventListener('mouseleave', () => close())
      wrap.addEventListener('focusin', open)
    }

    // focusul iese din zonă (click în altă parte, Tab) — se închide
    wrap.addEventListener('focusout', (e) => {
      if (!wrap.contains(e.relatedTarget)) close(0)
    })

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close(0)
    })
  })
}

/**
 * Notificări (pacient logat): „marchează totul ca citit" din panou și marcarea
 * unei notificări la click pe rând. Endpoint: user-notifications.read (POST,
 * `id` opțional). Contorul total vine din data-unread (panoul arată doar 5).
 */
export function initNotifications() {
  const wrap = document.querySelector('[data-psi-notif]')
  if (!wrap) return

  const url = wrap.dataset.readUrl
  const csrf = document.querySelector('meta[name="csrf-token"]')
  const token = csrf ? csrf.getAttribute('content') : ''
  let unread = parseInt(wrap.dataset.unread, 10) || 0

  // keepalive: cererea pleacă și dacă rândul apăsat navighează spre programare
  const post = (id) => fetch(url, {
    method: 'POST',
    headers: {
      'X-CSRF-TOKEN': token,
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(id ? { id } : {}),
    keepalive: true
  }).catch(() => {})

  const paint = () => {
    document.querySelectorAll('.psi-notif-btn').forEach((b) => {
      b.classList.toggle('has-unread', unread > 0)
      const label = unread > 0
        ? (b.dataset.labelUnread || '').replace(':count', String(unread))
        : (b.dataset.label || '')
      if (label) b.setAttribute('aria-label', label)
    })
    document.querySelectorAll('[data-psi-notif-count]').forEach((el) => {
      if (unread > 0) el.textContent = String(unread)
      else el.remove()
    })
    const readAll = wrap.querySelector('[data-psi-notif-readall]')
    if (readAll) readAll.hidden = unread === 0
  }

  const readAll = wrap.querySelector('[data-psi-notif-readall]')
  if (readAll) {
    readAll.addEventListener('click', () => {
      post(null)
      wrap.querySelectorAll('.psi-nt-row.is-unread').forEach((row) => row.classList.remove('is-unread'))
      unread = 0
      paint()
    })
  }

  wrap.addEventListener('click', (e) => {
    const row = e.target.closest('.psi-nt-row.is-unread')
    if (!row || !row.dataset.psiNotifId) return
    post(row.dataset.psiNotifId)
    row.classList.remove('is-unread')
    unread = Math.max(0, unread - 1)
    paint()
  })
}
